import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Stack, CircularProgress, Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { formatCurrency, formatDateTime, formatStatus } from '../../utils/formatters';
import { getPendingPayouts, processPayouts, getPayoutHistory } from '../../api/endpoints/payouts';

const PayoutList = () => {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [pendRes, histRes] = await Promise.all([
        getPendingPayouts(),
        getPayoutHistory({ limit: 50 }),
      ]);
      setPending(pendRes.data.data || []);
      setHistory(histRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (groupId, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      const group = pending.find((g) => g._id === groupId);
      if (group) {
        group.commissions.forEach((c) => {
          next[c._id] = checked;
        });
      }
      return next;
    });
  };

  const handleSelect = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getSelectedIds = () =>
    Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const handleProcessSelected = async () => {
    const ids = getSelectedIds();
    if (!ids.length) {
      toast.error('Select at least one commission to process');
      return;
    }
    try {
      setProcessing(true);
      await processPayouts(ids);
      toast.success(`Processed ${ids.length} payout(s)`);
      setSelected({});
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to process payouts');
    } finally {
      setProcessing(false);
    }
  };

  const groupedTotal = (group) =>
    group.commissions.reduce((sum, c) => sum + (c.adminCommission || 0), 0);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <PageHeader title="Payout Management" subtitle="Process and view astrologer payouts" />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Pending Payouts</Typography>
                {getSelectedIds().length > 0 && (
                  <Button variant="contained" size="small" onClick={handleProcessSelected} disabled={processing}>
                    {processing ? <CircularProgress size={16} /> : `Process Selected (${getSelectedIds().length})`}
                  </Button>
                )}
              </Stack>
              {pending.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No pending payouts</Typography>
              ) : (
                <Stack spacing={2}>
                  {pending.map((group) => {
                    const groupSelected = group.commissions.every((c) => selected[c._id]);
                    const someSelected = group.commissions.some((c) => selected[c._id]);
                    return (
                      <Card key={group._id} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ pb: 1 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Checkbox
                                size="small"
                                checked={groupSelected}
                                indeterminate={!groupSelected && someSelected}
                                onChange={(e) => handleSelectAll(group._id, e.target.checked)}
                              />
                              <Typography variant="subtitle2" fontWeight={600}>
                                {group.astrologer?.name || group.astrologerId?.name || 'Unknown Astrologer'}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={600} color="primary.main">
                              {formatCurrency(groupedTotal(group))}
                            </Typography>
                          </Stack>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox" />
                                  <TableCell>Service</TableCell>
                                  <TableCell align="right">Gross</TableCell>
                                  <TableCell align="right">Admin Share</TableCell>
                                  <TableCell>Date</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.commissions.map((c) => (
                                  <TableRow key={c._id} hover>
                                    <TableCell padding="checkbox">
                                      <Checkbox size="small" checked={!!selected[c._id]} onChange={() => handleSelect(c._id)} />
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={c.type || c.serviceType || '-'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(c.totalAmount)}</TableCell>
                                    <TableCell align="right">{formatCurrency(c.adminCommission)}</TableCell>
                                    <TableCell>{formatDateTime(c.createdAt)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Payout History</Typography>
              {history.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No payouts processed yet</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Astrologer</TableCell>
                        <TableCell align="right">Gross</TableCell>
                        <TableCell align="right">TDS</TableCell>
                        <TableCell align="right">Net</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((p, i) => (
                        <TableRow key={p._id || i}>
                          <TableCell>{formatDateTime(p.processedAt || p.createdAt)}</TableCell>
                          <TableCell><Typography variant="body2">{p.astrologer?.name || p.astrologerId?.name || '-'}</Typography></TableCell>
                          <TableCell align="right">{formatCurrency(p.grossAmount || p.totalAmount)}</TableCell>
                          <TableCell align="right">{formatCurrency(p.tdsAmount || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(p.netAmount || p.adminCommission || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PayoutList;
